import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Plus } from 'lucide-react';
import { CONTACT, PUBLIC_BOOKING_ENABLED } from '../../config';
import { useLocalizedUrl } from '../../utils/urlHelper';

// Piezas comunes del diseño B2B de la web pública (nov-2026), sacadas de la
// home (pages/Landing.jsx) para extenderlo al resto de páginas: azul marino,
// botones rectos, antetítulos en versalitas, filetes finos y nada de tarjetas
// redondeadas de colores.

export const NAVY = '#0b1d39';
export const BTN_LIGHT = 'inline-flex items-center justify-center gap-2 bg-white font-semibold px-7 py-4 hover:bg-blue-50 transition-colors';
export const BTN_GHOST = 'inline-flex items-center justify-center gap-2 border border-white/40 text-white font-semibold px-7 py-4 hover:bg-white/10 hover:border-white/70 transition-colors';

const CTA_LABELS = {
  es: { demo: 'Reserve su demostración', contact: 'Contacte con nosotros', phone: 'o llámenos al' },
  en: { demo: 'Book your demonstration', contact: 'Contact us', phone: 'or call us on' },
};

/** Destino y texto de la llamada principal (con las reservas apagadas, contacto). */
export const usePrimaryCta = (lang) => {
  const { getLocalizedUrl } = useLocalizedUrl();
  const l = CTA_LABELS[lang] || CTA_LABELS.es;
  return PUBLIC_BOOKING_ENABLED
    ? { to: getLocalizedUrl('/contacto/demo'), label: l.demo }
    : { to: getLocalizedUrl('/contacto'), label: l.contact };
};

/** Antetítulo de sección. `asIs`: sin versalitas (para «eCMR», «DeCA»…). */
export const Eyebrow = ({ children, light = false, asIs = false }) => (
  <p className={`text-xs font-semibold mb-4 ${asIs ? 'tracking-wide' : 'uppercase tracking-[0.18em]'} ${light ? 'text-blue-300' : 'text-primary-700'}`}>
    {children}
  </p>
);

/** Antetítulo + H2 (+ entradilla) de una sección. */
export const SectionIntro = ({ eyebrow, title, text, light = false, eyebrowAsIs = false, className = 'max-w-3xl mb-14' }) => (
  <div className={className}>
    {eyebrow && <Eyebrow light={light} asIs={eyebrowAsIs}>{eyebrow}</Eyebrow>}
    <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight leading-tight ${light ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
    {text && <p className={`mt-4 text-lg leading-relaxed ${light ? 'text-slate-300' : 'text-slate-600'}`}>{text}</p>}
  </div>
);

/**
 * Hero a pantalla completa en azul marino. Dos variantes:
 *  - image = { src, position }: foto de fondo con veladura (la home).
 *  - documentImage = { src, alt }: el documento real de BlueCMR a la derecha,
 *    asomando y cortado por el borde inferior (páginas de documento: así no
 *    todas las páginas llevan el mismo «texto + camión»). Desde 1280 px; por
 *    debajo, solo texto. La imagen debe ser más alta que el hero.
 */
export const PageHero = ({ eyebrow, title, text, image, documentImage, primary, secondary, compact = false, children }) => (
  <section className="relative overflow-hidden" style={{ backgroundColor: NAVY }}>
    {image && (
      <>
        <img src={image.src} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: image.position || '50% 50%' }} loading="eager" fetchpriority="high" />
        <div className="absolute inset-0 md:hidden" style={{ backgroundColor: `${NAVY}cc` }} />
        <div className="absolute inset-0 hidden md:block" style={{ background: `linear-gradient(90deg, ${NAVY} 0%, ${NAVY}e6 38%, ${NAVY}80 62%, ${NAVY}26 100%)` }} />
      </>
    )}
    {documentImage && (
      <div className="pointer-events-none absolute inset-0 hidden xl:block" aria-hidden={documentImage.alt ? undefined : true}>
        <div className="mx-auto h-full max-w-7xl px-8">
          <div className="relative ml-auto h-full w-[44%] max-w-[540px]">
            <img
              src={documentImage.src}
              alt={documentImage.alt || ''}
              className="absolute left-0 right-0 top-14 w-full bg-white shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)] ring-1 ring-black/10"
              loading="eager"
              fetchpriority="high"
            />
            {/* fundido hacia el borde inferior del hero */}
            <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: `linear-gradient(to bottom, ${NAVY}00, ${NAVY})` }} />
          </div>
        </div>
      </div>
    )}
    <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${compact ? 'py-20 md:py-24' : 'py-24 md:py-32'}`}>
      <div className={documentImage ? 'max-w-3xl xl:max-w-[52%]' : image ? 'max-w-3xl lg:max-w-2xl' : 'max-w-3xl'}>
        {eyebrow && <p className="text-sm sm:text-[15px] font-semibold tracking-wide text-blue-300 mb-6">{eyebrow}</p>}
        <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-semibold tracking-tight text-white leading-[1.08]">{title}</h1>
        {text && <p className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl">{text}</p>}
        {children}
        {(primary || secondary) && (
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            {primary && (
              <Link to={primary.to} className={BTN_LIGHT} style={{ color: NAVY }}>
                {primary.label}
                <ArrowRight className="w-5 h-5" />
              </Link>
            )}
            {secondary && (secondary.href
              ? <a href={secondary.href} target="_blank" rel="noopener noreferrer" className={BTN_GHOST}>{secondary.label}</a>
              : <Link to={secondary.to} className={BTN_GHOST}>{secondary.label}</Link>
            )}
          </div>
        )}
      </div>
    </div>
  </section>
);

/**
 * Franja de cabecera azul marino, más baja que PageHero, para páginas de trámite
 * (contacto, formulario, llamada, reserva): antetítulo, H1 y entradilla.
 */
export const PageBand = ({ eyebrow, title, text, children }) => (
  <section style={{ backgroundColor: NAVY }}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
      <div className="max-w-3xl">
        {eyebrow && <p className="text-sm sm:text-[15px] font-semibold tracking-wide text-blue-300 mb-4">{eyebrow}</p>}
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight">{title}</h1>
        {text && <p className="mt-4 text-lg text-slate-300 leading-relaxed">{text}</p>}
        {children}
      </div>
    </div>
  </section>
);

/**
 * Documento real con marcas numeradas y su leyenda al lado.
 * notes = [{ x, y, title, text }] con x/y en % de la imagen (centro de la marca).
 */
export const AnnotatedDocument = ({ src, alt, width, height, notes }) => (
  <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
    <figure className="lg:col-span-7">
      <div className="relative bg-white ring-1 ring-slate-200 shadow-[0_24px_50px_-24px_rgba(15,23,42,0.45)]">
        <img src={src} alt={alt} width={width} height={height} loading="lazy" decoding="async" className="block h-auto w-full" />
        {notes.map((n, i) => (
          <span
            key={n.title}
            aria-hidden="true"
            className="absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary-700 text-[11px] font-semibold text-white ring-2 ring-white sm:h-7 sm:w-7 sm:text-xs sm:ring-4"
            style={{ left: `${n.x}%`, top: `${n.y}%` }}
          >
            {i + 1}
          </span>
        ))}
      </div>
    </figure>
    <ol className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
      {notes.map((n, i) => (
        <li key={n.title} className="flex gap-4">
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-700 text-xs font-semibold text-white">{i + 1}</span>
          <div>
            <h3 className="font-semibold text-slate-900">{n.title}</h3>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">{n.text}</p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

/** Preguntas frecuentes desplegables con filetes finos. items = [{ q, a }]. */
export const FaqList = ({ items }) => (
  <div className="border-b border-slate-200">
    {items.map((item) => (
      <details key={item.q} className="group border-t border-slate-200">
        <summary className="flex cursor-pointer list-none items-start justify-between gap-6 py-5 text-left [&::-webkit-details-marker]:hidden">
          <h3 className="font-semibold text-slate-900">{item.q}</h3>
          <Plus className="mt-0.5 w-5 h-5 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-45" />
        </summary>
        <p className="pb-6 pr-10 text-[15px] text-slate-600 leading-relaxed">{item.a}</p>
      </details>
    ))}
  </div>
);

/** Esquema FAQPage a partir de los mismos items que se ven en la página. */
export const faqSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
});

/** Lista con marcas de verificación (puntos de una sección). */
export const CheckList = ({ items, className = 'mt-4 space-y-2.5' }) => (
  <ul className={className}>
    {items.map((item) => (
      <li key={item} className="flex items-start gap-2.5 text-[15px] text-slate-600 leading-relaxed">
        <Check className="w-4 h-4 mt-1 flex-shrink-0 text-primary-700" strokeWidth={2} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/**
 * Cierre en azul marino con la llamada principal y el teléfono.
 * `query` (p. ej. '?origin=cargadores&profile=shipper') titula y cualifica la reserva.
 */
export const ClosingCta = ({ title, text, lang = 'es', query = '' }) => {
  const primary = usePrimaryCta(lang);
  const l = CTA_LABELS[lang] || CTA_LABELS.es;
  return (
    <section className="py-24" style={{ backgroundColor: NAVY }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">{title}</h2>
        {text && <p className="mt-4 text-lg text-slate-300 leading-relaxed">{text}</p>}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-4">
          <Link to={`${primary.to}${query}`} className={BTN_LIGHT} style={{ color: NAVY }}>
            {primary.label}
            <ArrowRight className="w-5 h-5" />
          </Link>
          <span className="text-slate-300">
            {l.phone}{' '}
            <a href={CONTACT.phoneHref} className="font-semibold text-white hover:underline whitespace-nowrap">{CONTACT.phoneDisplay}</a>
          </span>
        </div>
      </div>
    </section>
  );
};
